// Training / studiereis detail page (PDP):
// - sticky scroll-to nav with scroll-spy
// - date selector in the info card(s)
// - mobile bottom bar (shown once the info card has scrolled out of view)
// - trainers scroller (next arrow + end fade)
// All hooks are data-attributes, see templates/changekitchen/_components/academy/pdp/.

const SCROLL_LOCK_MS = 800

// Modules can be re-initialised (window.HappyHorizon.reinitialize), so bind each element once
const bindOnce = (element) => {
    if (element.dataset.pdpBound) {
        return false
    }

    element.dataset.pdpBound = '1'

    return true
}

const scrollBehavior = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'

const initNav = () => {
    const nav = document.querySelector('[data-pdp-nav]')

    if (!nav || !bindOnce(nav)) {
        return
    }

    const list = nav.querySelector('[data-pdp-nav-list]')
    const fadeLeft = nav.querySelector('[data-pdp-fade="left"]')
    const fadeRight = nav.querySelector('[data-pdp-fade="right"]')
    const links = [...nav.querySelectorAll('[data-pdp-nav-link]')]
    const sections = links
        .map((link) => document.getElementById(link.dataset.pdpNavLink))
        .filter(Boolean)
    const visible = new Set()
    let locked = false
    let lockTimer = null

    const updateFades = () => {
        const max = list.scrollWidth - list.clientWidth
        fadeLeft?.classList.toggle('opacity-0', list.scrollLeft < 4)
        fadeRight?.classList.toggle('opacity-0', max < 4 || list.scrollLeft > max - 4)
    }

    const setActive = (id) => {
        links.forEach((link) => {
            const active = link.dataset.pdpNavLink === id
            active ? link.setAttribute('aria-current', 'true') : link.removeAttribute('aria-current')

            // Keep the active item in view in the (horizontally scrollable) mobile nav
            if (active && list.scrollWidth > list.clientWidth) {
                const listRect = list.getBoundingClientRect()
                const linkRect = link.getBoundingClientRect()
                list.scrollTo({
                    left:
                        list.scrollLeft +
                        linkRect.left -
                        listRect.left -
                        (list.clientWidth - linkRect.width) / 2,
                    behavior: scrollBehavior(),
                })
            }
        })
    }

    links.forEach((link) => {
        link.addEventListener('click', (event) => {
            const target = document.getElementById(link.dataset.pdpNavLink)

            if (!target) {
                return
            }

            event.preventDefault()
            locked = true
            clearTimeout(lockTimer)
            lockTimer = setTimeout(() => {
                locked = false
            }, SCROLL_LOCK_MS)

            setActive(link.dataset.pdpNavLink)
            // Sections carry scroll-mt-* so they land just below the sticky nav
            target.scrollIntoView({ behavior: scrollBehavior(), block: 'start' })
            history.replaceState(null, '', `#${target.id}`)
        })
    })

    // A section is "current" while it crosses the band between the nav and 40% of the viewport
    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                entry.isIntersecting ? visible.add(entry.target) : visible.delete(entry.target)
            })

            if (locked) {
                return
            }

            const current = sections.filter((section) => visible.has(section)).pop()

            if (current) {
                setActive(current.id)
            }
        },
        { rootMargin: '-72px 0px -60% 0px' },
    )

    sections.forEach((section) => observer.observe(section))

    // Sidebar sits above the nav at its start position (z-30), but once the nav is stuck to the
    // top it must go below it (z-10), otherwise it paints over the nav at the end of the article
    const sidebar = document.querySelector('[data-pdp-sidebar]')

    if (sidebar) {
        new IntersectionObserver(
            ([entry]) => {
                // ratio < 1 with the -1px top margin = nav touches the top edge; the top <= 0
                // check ignores the case where the nav is still below the viewport
                const stuck = entry.intersectionRatio < 1 && entry.boundingClientRect.top <= 0
                sidebar.toggleAttribute('data-stuck', stuck)
            },
            { threshold: [1], rootMargin: '-1px 0px 0px 0px' },
        ).observe(nav)
    }

    list.addEventListener('scroll', updateFades, { passive: true })
    window.addEventListener('resize', updateFades)
    setActive(links[0]?.dataset.pdpNavLink)
    updateFades()
}

// The sticky mobile bar follows the last selected date, from either date selector
const updateMobileBar = (option, waitlist) => {
    const bar = document.querySelector('[data-pdp-bar]')
    const cta = bar?.querySelector('[data-pdp-bar-cta]')

    if (!cta) {
        return
    }

    const optie = bar.querySelector('[data-pdp-bar-optie]')
    const contact = bar.querySelector('[data-pdp-bar-contact]')

    cta.href = option.dataset.url
    cta.querySelector('[data-pdp-bar-cta-label]').textContent = waitlist ? 'Wachtlijst' : 'Inschrijven'
    optie.href = option.dataset.optieUrl
    optie.classList.toggle('hidden', waitlist)
    contact.classList.toggle('hidden', !waitlist)
}

const selectDate = (root, option) => {
    const card = root.closest('[data-pdp-card]')
    const waitlist = option.dataset.waitlist === '1'

    root.querySelector('[data-date-label]').textContent = option.dataset.label
    root.querySelectorAll('[data-date-option]').forEach((item) => {
        item.setAttribute('aria-selected', String(item === option))
    })

    updateMobileBar(option, waitlist)

    if (!card) {
        return
    }

    const cta = card.querySelector('[data-pdp-cta]')
    const optie = card.querySelector('[data-pdp-optie]')
    const location = card.querySelector('[data-pdp-location]')

    if (cta) {
        cta.href = option.dataset.url
        cta.querySelector('[data-pdp-cta-label]').textContent = waitlist ? 'Wachtlijst' : 'Inschrijven'
        cta.querySelector('[data-pdp-cta-date]').textContent = `op ${option.dataset.short}`
    }

    if (optie) {
        optie.href = option.dataset.optieUrl
        optie.classList.toggle('hidden', waitlist)
    }

    if (location) {
        location.textContent = option.dataset.location || 'In overleg'
    }
}

const initDateSelectors = () => {
    document.querySelectorAll('[data-date-selector]').forEach((root) => {
        if (!bindOnce(root)) {
            return
        }

        const toggle = root.querySelector('[data-date-toggle]')
        const list = root.querySelector('[data-date-list]')
        const options = [...root.querySelectorAll('[data-date-option]')]

        const close = () => {
            list.hidden = true
            toggle.setAttribute('aria-expanded', 'false')
        }

        const open = () => {
            list.hidden = false
            toggle.setAttribute('aria-expanded', 'true')
            ;(options.find((option) => option.getAttribute('aria-selected') === 'true') ?? options[0])?.focus()
        }

        toggle.addEventListener('click', () => (list.hidden ? open() : close()))

        options.forEach((option) => {
            option.addEventListener('click', () => {
                selectDate(root, option)
                close()
                toggle.focus()
            })
        })

        root.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && !list.hidden) {
                close()
                toggle.focus()
            }
        })

        document.addEventListener('click', (event) => {
            if (!root.contains(event.target)) {
                close()
            }
        })
    })
}

const initMobileBar = () => {
    const bar = document.querySelector('[data-pdp-bar]')
    // The mobile card is the first one in the DOM (the sidebar copy is display:none on mobile)
    const card = document.querySelector('[data-pdp-card]')
    const footer = document.querySelector('footer')

    if (!bar || !card || !bindOnce(bar)) {
        return
    }

    let cardGone = false
    let footerVisible = false

    const render = () => {
        const show = cardGone && !footerVisible

        bar.classList.toggle('translate-y-full', !show)
        bar.classList.toggle('opacity-0', !show)
        bar.classList.toggle('pointer-events-none', !show)
        bar.classList.toggle('translate-y-0', show)
        bar.classList.toggle('opacity-100', show)
        bar.toggleAttribute('inert', !show)
    }

    // "Gone" = scrolled past (above the viewport), not merely below it
    // Batched entries are chronological: the last one is the current state
    new IntersectionObserver((entries) => {
        const entry = entries[entries.length - 1]
        cardGone = !entry.isIntersecting && entry.boundingClientRect.top < 0
        render()
    }).observe(card)

    if (footer) {
        new IntersectionObserver((entries) => {
            footerVisible = entries[entries.length - 1].isIntersecting
            render()
        }).observe(footer)
    }
}

const initTrainers = () => {
    document.querySelectorAll('[data-pdp-trainers]').forEach((root) => {
        const list = root.querySelector('[data-pdp-trainers-list]')
        const next = root.querySelector('[data-pdp-trainers-next]')
        const prev = root.querySelector('[data-pdp-trainers-prev]')
        const fade = root.querySelector('[data-pdp-trainers-fade]')
        const fadeStart = root.querySelector('[data-pdp-trainers-fade-start]')

        if (!list || !next || !prev || !bindOnce(root)) {
            return
        }

        // Each side only shows while there is something to scroll to on that side
        const update = () => {
            const atStart = list.scrollLeft <= 4
            const atEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 4

            prev.disabled = atStart
            next.disabled = atEnd
            fadeStart?.classList.toggle('opacity-0', atStart)
            fade?.classList.toggle('opacity-0', atEnd)
        }

        const scrollByCard = (direction) => {
            const card = list.firstElementChild
            const gap = parseFloat(getComputedStyle(list).columnGap) || 0

            list.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: scrollBehavior() })
        }

        next.addEventListener('click', () => scrollByCard(1))
        prev.addEventListener('click', () => scrollByCard(-1))
        list.addEventListener('scroll', update, { passive: true })
        window.addEventListener('resize', update)
        update()
    })
}

export const pdpNav = {
    init() {
        initNav()
        initDateSelectors()
        initMobileBar()
        initTrainers()
    },
}
